import React, { useCallback } from 'react';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { PlusIcon } from '@phosphor-icons/react';
import {
  useNavigate,
  Outlet,
  useLocation,
  useRouter,
} from '@tanstack/react-router';
import {
  ForegroundAgentChat,
  TempReportPayload,
} from '@/components/common/ForegroundAgentChat';
import { useAgentPanel } from '@/context/agent-panel.context';

function RebateReportsIndexPage() {
  const navigate = useNavigate();
  const router = useRouter();
  const location = useLocation();
  const { open: openChat } = useAgentPanel();

  // Check if we're on a child route (not the index page itself)
  const isChildRoute = location.pathname !== '/rebates/reports';

  // Handle AI-created temp report
  const handleCreateTempReport = useCallback(
    (payload: TempReportPayload) => {
      // Always override timeFilterPreset to 'all' for AI-created tables
      const tablesWithDefaults = payload.tables.map((table) => ({
        ...table,
        timeFilterPreset: 'all',
      }));

      // Navigate to temp report page with state using router.navigate
      // which allows passing state properly
      router.navigate({
        to: '/rebates/reports/temp',
        state: {
          title: payload.title,
          tables: tablesWithDefaults,
        } as Record<string, unknown>,
      });
    },
    [router],
  );

  // Handle click on create card
  const handleCreateClick = useCallback(() => {
    openChat();
  }, [openChat]);

  // If we're on a child route, just render the Outlet
  if (isChildRoute) {
    return <Outlet />;
  }

  // Otherwise, render the index page
  return (
    <DashboardLayout>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Rebate Reports</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Create custom reports with AI assistance
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Create Report Tile */}
          <Card
            className="flex flex-col hover:shadow-lg transition-shadow cursor-pointer border-dashed border-2 hover:border-primary"
            onClick={handleCreateClick}
          >
            <CardHeader className="flex-1">
              <div className="flex items-center justify-center p-6">
                <div className="p-4 bg-primary/10 rounded-full">
                  <PlusIcon className="h-10 w-10 text-primary" />
                </div>
              </div>
              <CardTitle className="text-center">
                Create Report with AI
              </CardTitle>
              <CardDescription className="text-center mt-2">
                Describe the report you want and AI will create it for you. For
                example: &quot;Create a report showing month by division and
                another table for supplier by rule tag&quot;
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="text-sm text-center text-muted-foreground">
                Click to open AI assistant
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* AI Chat Assistant */}
      <ForegroundAgentChat
        moduleKey="rebates"
        currentPage="/rebates/reports"
        onCreateTempReport={handleCreateTempReport}
      />
    </DashboardLayout>
  );
}

export default RebateReportsIndexPage;
